import { useState } from "react";
import { API_BASE } from "../../utils/api";
import { useIsMobile } from "../../hooks/useIsMobile";

interface Props {
  listingId: string;
}

const SECTION_META = [
  { icon: "\u2B50", color: "#D4A853", border: "rgba(212,168,83,0.2)", bg: "rgba(212,168,83,0.05)" },
  { icon: "\uD83D\uDCA1", color: "#22C55E", border: "rgba(34,197,94,0.2)", bg: "rgba(34,197,94,0.05)" },
  { icon: "\u26A0\uFE0F", color: "#F97316", border: "rgba(249,115,22,0.2)", bg: "rgba(249,115,22,0.05)" },
  { icon: "\uD83D\uDCB0", color: "#3B82F6", border: "rgba(59,130,246,0.2)", bg: "rgba(59,130,246,0.05)" },
  { icon: "\uD83D\uDCCB", color: "#A855F7", border: "rgba(168,85,247,0.2)", bg: "rgba(168,85,247,0.05)" },
];

interface ReportSection {
  title: string;
  content: string;
}

function parseReport(text: string): ReportSection[] {
  const sections: ReportSection[] = [];
  const lines = text.split("\n");
  let current: { title: string; lines: string[] } | null = null;

  for (const line of lines) {
    // "1. 제목", "**1. 제목**", "1. **제목**" 등 패턴 지원
    const match = line.match(/^\s*\*{0,2}(\d+)\.\s+(.+?)\*{0,2}\s*$/);
    if (match) {
      if (current) {
        sections.push({ title: current.title, content: current.lines.join("\n").trim() });
      }
      current = { title: match[2].replace(/\*\*/g, "").trim(), lines: [] };
    } else if (current) {
      current.lines.push(line);
    }
  }
  if (current) {
    sections.push({ title: current.title, content: current.lines.join("\n").trim() });
  }
  return sections;
}

function extractStarRating(text: string): number {
  // "★★★★☆" 패턴
  const starFull = text.match(/★+/);
  if (starFull) return Math.min(5, starFull[0].length);
  // "4/5", "4점" 패턴
  const num = text.match(/(\d+(?:\.\d+)?)\s*[\/점]/);
  if (num) return Math.min(5, Math.round(parseFloat(num[1])));
  return 0;
}

function cleanContent(text: string): string {
  return text
    .replace(/\*\*/g, "")
    .replace(/^\s*[-•]\s*/gm, "• ")
    .trim();
}

export function ListingAIReport({ listingId }: Props) {
  const m = useIsMobile();
  const [report, setReport] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cached, setCached] = useState(false);

  async function fetchReport(force = false) {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/listings/${listingId}/ai-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force }),
      });
      const d = await res.json() as any;
      if (!res.ok) throw new Error(d.error || "AI 분석 실패");
      setReport(d.report);
      setCached(!!d.cached);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const sections = report ? parseReport(report) : [];

  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border-light)",
      borderTop: "3px solid rgba(212,168,83,0.45)",
      borderRadius: m ? 12 : 16,
      padding: m ? "18px 14px" : "24px 28px",
    }}>
      {/* 헤더 */}
      <h2 style={{
        fontSize: m ? 15 : 16, fontWeight: 700,
        color: "var(--text-primary)", margin: m ? "0 0 16px" : "0 0 20px",
      }}>
        {"\uD83E\uDD16"} AI {"\uBD84\uC11D"} {"\uB9AC\uD3EC\uD2B8"}
      </h2>

      {/* 초기 상태 */}
      {!report && !loading && !error && (
        <div style={{ textAlign: "center", padding: m ? "20px 0 8px" : "28px 0 12px" }}>
          <div style={{ fontSize: m ? 48 : 60, marginBottom: 14, lineHeight: 1 }}>{"\uD83E\uDD16"}</div>
          <p style={{
            color: "#94A3B8", fontSize: m ? 13 : 14,
            lineHeight: 1.7, margin: "0 auto 20px", maxWidth: 340,
          }}>
            {"AI\uAC00 \uC774 \uB9E4\uBB3C\uC758 \uD22C\uC790 \uAC00\uCE58\uC640 \uC218\uC775\uD654 \uAC00\uB2A5\uC131,"}<br />
            {"\uB9AC\uC2A4\uD06C\uB97C \uC885\uD569 \uBD84\uC11D\uD574 \uB4DC\uB9BD\uB2C8\uB2E4."}
          </p>
          <button
            onClick={() => fetchReport(false)}
            style={{
              padding: m ? "12px 28px" : "13px 36px",
              borderRadius: 10, border: "none",
              background: "linear-gradient(135deg, #D4A853 0%, #C49A3C 100%)",
              color: "#080E1A", fontSize: m ? 14 : 15, fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 18px rgba(212,168,83,0.3)",
            }}
          >
            {"\u2728 AI \uBD84\uC11D \uC2DC\uC791"}
          </button>
        </div>
      )}

      {/* 로딩 */}
      {loading && (
        <div style={{ textAlign: "center", padding: m ? "28px 0" : "40px 0" }}>
          <div style={{
            width: 40, height: 40,
            border: "3px solid rgba(212,168,83,0.2)",
            borderTopColor: "#D4A853",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto",
          }} />
          <p style={{ color: "#94A3B8", marginTop: 16, fontSize: 14 }}>
            {"AI\uAC00 \uBD84\uC11D \uC911\uC785\uB2C8\uB2E4..."}
          </p>
          <p style={{ color: "#64748B", fontSize: 12, marginTop: 4 }}>
            {"\uBCF4\uD1B5 10~20\uCD08 \uC18C\uC694\uB429\uB2C8\uB2E4"}
          </p>
        </div>
      )}

      {/* 에러 */}
      {error && !loading && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{"\u274C"}</div>
          <p style={{ color: "#EF4444", fontSize: 14, marginBottom: 16 }}>{error}</p>
          <button
            onClick={() => fetchReport(false)}
            style={{
              padding: "10px 24px", borderRadius: 8,
              border: "none",
              background: "linear-gradient(135deg, #D4A853 0%, #C49A3C 100%)",
              color: "#080E1A", fontSize: 14, fontWeight: 700, cursor: "pointer",
            }}
          >
            {"\uB2E4\uC2DC \uC2DC\uB3C4"}
          </button>
        </div>
      )}

      {/* 리포트 결과 */}
      {report && !loading && (
        <>
          {cached && (
            <div style={{
              fontSize: 11, color: "#64748B",
              marginBottom: 14, textAlign: "right",
            }}>
              {"\uD83D\uDCE6 \uCE90\uC2DC\uB41C \uBD84\uC11D \uACB0\uACFC (\uCD5C\uB300 24\uC2DC\uAC04 \uC720\uD6A8)"}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: m ? 10 : 12 }}>
            {sections.map((sec, i) => {
              const meta = SECTION_META[i] ?? SECTION_META[4];
              const starCount = i === 0 ? extractStarRating(sec.content) : 0;
              return (
                <div key={i} style={{
                  background: meta.bg,
                  border: `1px solid ${meta.border}`,
                  borderLeft: `3px solid ${meta.color}`,
                  borderRadius: 12,
                  padding: m ? "14px 14px" : "16px 20px",
                }}>
                  {/* 섹션 헤더 */}
                  <div style={{
                    display: "flex", alignItems: "center",
                    gap: 8, marginBottom: 10,
                    flexWrap: "wrap" as const,
                  }}>
                    <span style={{ fontSize: m ? 17 : 19 }}>{meta.icon}</span>
                    <span style={{
                      fontSize: m ? 13 : 14, fontWeight: 700,
                      color: meta.color, flex: 1,
                    }}>
                      {sec.title}
                    </span>
                    {starCount > 0 && (
                      <span style={{ display: "flex", gap: 2 }}>
                        {Array.from({ length: 5 }, (_, j) => (
                          <span key={j} style={{
                            fontSize: m ? 15 : 17,
                            color: j < starCount ? "#D4A853" : "rgba(212,168,83,0.2)",
                          }}>
                            {"\u2605"}
                          </span>
                        ))}
                        <span style={{
                          fontSize: 12, color: "#D4A853", fontWeight: 700,
                          marginLeft: 4, alignSelf: "center",
                        }}>
                          {starCount}/5
                        </span>
                      </span>
                    )}
                  </div>
                  {/* 섹션 내용 */}
                  <div style={{
                    fontSize: m ? 13 : 14,
                    color: "#CBD5E1", lineHeight: 1.8,
                    whiteSpace: "pre-line" as const,
                  }}>
                    {cleanContent(sec.content)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 다시 생성 버튼 */}
          <div style={{ textAlign: "center", marginTop: m ? 16 : 20 }}>
            <button
              onClick={() => fetchReport(true)}
              disabled={loading}
              style={{
                padding: "9px 22px", borderRadius: 8,
                border: "1px solid rgba(148,163,184,0.3)",
                background: "transparent",
                color: "#64748B", fontSize: 13, fontWeight: 600,
                cursor: "pointer", transition: "all 0.2s",
              }}
            >
              {"\uD83D\uDD04 \uB9AC\uD3EC\uD2B8 \uB2E4\uC2DC \uC0DD\uC131"}
            </button>
            <p style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
              {"\uB2E4\uC2DC \uC0DD\uC131 \uC2DC AI\uB97C \uC0C8\uB85C \uD638\uCD9C\uD569\uB2C8\uB2E4 (\uAE30\uC874 \uCE90\uC2DC \uC0AD\uC81C)"}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
